/**
 * CR220: Preview Data Source Selector
 * 
 * Allows selection of data source for Layout Studio preview:
 * - Live (Public) - same as public site
 * - Admin (Prod) - admin snapshot
 * - Staging - staging environment
 * - Batch Run - specific batch run output
 * - Mock - static mock data (fallback)
 */

import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Globe,
  Shield,
  FlaskConical,
  Database,
  Sparkles,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import type { PreviewDataSource, PreviewDealsMeta, BatchRunOption } from "@/hooks/useLayoutPreviewDeals";
import { formatDistanceToNow } from "date-fns";

// ================================================================
// TYPES
// ================================================================

interface PreviewDataSourceSelectorProps {
  source: PreviewDataSource;
  onSourceChange: (source: PreviewDataSource) => void;
  batchRunId: string | null;
  onBatchRunChange: (id: string | null) => void;
  batchRuns: BatchRunOption[];
  batchRunsLoading: boolean;
  onRefreshBatchRuns: () => void;
  meta: PreviewDealsMeta;
  loading: boolean;
  onRefresh: () => void;
  compact?: boolean;
}

// ================================================================
// SOURCE CONFIG
// ================================================================

interface SourceConfig {
  id: PreviewDataSource;
  label: string;
  shortLabel: string;
  description: string;
  icon: React.ElementType;
  badgeVariant: "default" | "secondary" | "outline" | "destructive";
}

const SOURCE_CONFIG: SourceConfig[] = [
  {
    id: "live",
    label: "Live (Public)",
    shortLabel: "Live",
    description: "Same data as the public site",
    icon: Globe,
    badgeVariant: "default",
  },
  {
    id: "admin",
    label: "Admin (Prod)",
    shortLabel: "Admin",
    description: "Internal admin snapshot",
    icon: Shield,
    badgeVariant: "secondary",
  },
  {
    id: "staging",
    label: "Staging",
    shortLabel: "Staging",
    description: "Staging environment deals",
    icon: FlaskConical,
    badgeVariant: "outline",
  },
  {
    id: "batch",
    label: "Batch Run",
    shortLabel: "Batch",
    description: "Output from a specific batch run",
    icon: Database,
    badgeVariant: "outline",
  },
  {
    id: "mock",
    label: "Mock Data",
    shortLabel: "Mock",
    description: "Static mock data for testing",
    icon: Sparkles,
    badgeVariant: "outline",
  },
];

// ================================================================
// COMPONENT
// ================================================================

export function PreviewDataSourceSelector({
  source,
  onSourceChange,
  batchRunId,
  onBatchRunChange,
  batchRuns,
  batchRunsLoading,
  onRefreshBatchRuns,
  meta,
  loading,
  onRefresh,
  compact = false,
}: PreviewDataSourceSelectorProps) {
  const currentConfig = useMemo(
    () => SOURCE_CONFIG.find((s) => s.id === source) || SOURCE_CONFIG[0],
    [source]
  );

  const lastRefreshedText = useMemo(() => {
    if (!meta.lastRefreshed) return null;
    try {
      return formatDistanceToNow(new Date(meta.lastRefreshed), { addSuffix: true });
    } catch {
      return null;
    }
  }, [meta.lastRefreshed]);

  const batchRunOptions = useMemo(() => {
    return batchRuns.map((run) => {
      const date = new Date(run.created_at);
      const dateStr = date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const timeStr = date.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
      const dealCount = run.deduped_count ?? run.total_staged_items ?? 0;
      return {
        id: run.id,
        label: `${dateStr} ${timeStr} (${dealCount} deals)`,
        status: run.status,
      };
    });
  }, [batchRuns]);

  const CurrentIcon = currentConfig.icon;

  return (
    <div className="space-y-2">
      {/* Row 1: Source selector + status */}
      <div className="flex items-center gap-2 flex-wrap">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5">
                <CurrentIcon className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">Data:</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="text-xs">{currentConfig.description}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <Select value={source} onValueChange={(v) => onSourceChange(v as PreviewDataSource)}>
          <SelectTrigger className="h-7 w-[140px] text-xs">
            <SelectValue placeholder="Select source..." />
          </SelectTrigger>
          <SelectContent>
            {SOURCE_CONFIG.map((config) => {
              const Icon = config.icon;
              return (
                <SelectItem key={config.id} value={config.id} className="text-xs">
                  <span className="flex items-center gap-2">
                    <Icon className="h-3.5 w-3.5" />
                    {compact ? config.shortLabel : config.label}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        {/* Batch run selector (only when source is 'batch') */}
        {source === "batch" && (
          <>
            <Separator orientation="vertical" className="h-4" />
            <Select 
              value={batchRunId || ""} 
              onValueChange={(v) => onBatchRunChange(v || null)}
            >
              <SelectTrigger className="h-7 w-[180px] text-xs">
                <SelectValue placeholder="Select batch run..." />
              </SelectTrigger>
              <SelectContent>
                {batchRunsLoading ? (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : batchRunOptions.length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center py-2">
                    No batch runs found
                  </div>
                ) : (
                  batchRunOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id} className="text-xs">
                      <span className="flex items-center gap-2">
                        {option.label}
                        {option.status === "COMPLETE" && (
                          <CheckCircle2 className="h-3 w-3 text-primary" />
                        )}
                      </span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={onRefreshBatchRuns}
              disabled={batchRunsLoading}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${batchRunsLoading ? "animate-spin" : ""}`} />
            </Button>
          </>
        )}

        <Separator orientation="vertical" className="h-4" />

        {/* Refresh button */}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={onRefresh}
          disabled={loading}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Row 2: Context header (meta info) */}
      <div className="flex items-center gap-2 flex-wrap text-[10px] text-muted-foreground bg-muted/30 rounded px-2 py-1">
        {/* Source badge */}
        <Badge variant={currentConfig.badgeVariant} className="text-[10px] h-5">
          {currentConfig.shortLabel}
        </Badge>

        {/* Snapshot/Batch ID */}
        {meta.snapshotId && (
          <span className="font-mono">
            snap: {meta.snapshotId.slice(0, 8)}...
          </span>
        )}
        {meta.batchRunId && source === "batch" && (
          <span className="font-mono">
            batch: {meta.batchRunId.slice(0, 8)}...
          </span>
        )}

        <Separator orientation="vertical" className="h-3" />

        {/* Deal count */}
        <span className="flex items-center gap-1">
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : meta.error ? (
            <AlertCircle className="h-3 w-3 text-destructive" />
          ) : (
            <CheckCircle2 className="h-3 w-3 text-primary" />
          )}
          {meta.dealCount} deals
        </span>

        <Separator orientation="vertical" className="h-3" />

        {/* Last refreshed */}
        {lastRefreshedText && (
          <span>Refreshed {lastRefreshedText}</span>
        )}

        {/* Error indicator */}
        {meta.error && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Error
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[300px]">
                <p className="text-xs">{meta.error}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}
